import React, { useState, useEffect } from "react";
import {
  Alert,
  Snackbar,
  Box,
  Typography,
  Button,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AlertManager from '../services/AlertManager';

export default function SystemAlert({
  open,
  severity,
  message,
  alerts = [],
  onClose,
  onDismissAlert,
  showMultiple = false
}) {
  const [isOpen, setIsOpen] = useState(open);
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsOpen(open);

    // Only auto-hide non-persistent alerts or single alert mode without persistent flag
    if (open && !showMultiple) {
      // Check if the single alert is persistent
      const singleAlert = message ? { severity, message } : (alerts.length === 1 ? alerts[0] : null);
      const isPersistent = singleAlert?.persistent || false;

      if (!isPersistent) {
        const timer = setTimeout(() => {
          setIsOpen(false);
          if (onClose) onClose();
        }, 5000);

        return () => clearTimeout(timer);
      }
    }

    // For multiple alerts mode, check if the primary alert is persistent
    if (open && showMultiple && alerts.length > 0) {
      const primaryAlert = alerts[0];
      const isPersistent = primaryAlert?.persistent || false;

      if (!isPersistent) {
        const timer = setTimeout(() => {
          setIsOpen(false);
          if (onClose) onClose();
        }, 8000); // Longer timeout for multiple alerts

        return () => clearTimeout(timer);
      }
    }
  }, [open, showMultiple, onClose, alerts, message, severity]);

  const handleClose = () => {
    setIsOpen(false);
    if (onClose) onClose();
  };

  const handleDismissAlert = (alertId, alertType) => {
    if (onDismissAlert) {
      onDismissAlert(alertId, alertType);
    } else {
      AlertManager.dismissAlert(alertId, alertType);
    }
  };

  const handleAlertAction = (action) => {
    if (action.action === 'navigate') {
      navigate(action.target, action.state ? { state: action.state } : {});
    }
    handleClose();
  };

  // Single alert mode (legacy support)
  if (!showMultiple && (message || (alerts.length === 1))) {
    // If severity is error, make alert persistent
    const singleAlert = message ? { severity, message, persistent: severity === 'error' } : alerts[0];

    return (
      <Snackbar
        open={isOpen}
        autoHideDuration={singleAlert.persistent ? null : 5000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={handleClose}
      >
        <Alert severity={singleAlert.severity || severity} onClose={handleClose}>
          {singleAlert.message || message}
        </Alert>
      </Snackbar>
    );
  }

  // Multiple alerts mode
  if (!showMultiple || alerts.length === 0) {
    return null;
  }

  const primaryAlert = alerts[0];
  const additionalAlerts = alerts.slice(1);

  return (
    <Snackbar
      open={isOpen}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      onClose={handleClose}
      sx={{ maxWidth: '600px' }}
    >
      <Alert
        severity={primaryAlert.severity}
        sx={{ width: '100%', maxWidth: '600px' }}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {additionalAlerts.length > 0 && (
              <Button
                size="small"
                onClick={() => setExpanded(!expanded)}
                endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ color: 'inherit', minWidth: 'auto' }}
              >
                {expanded ? 'Less' : `+${additionalAlerts.length}`}
              </Button>
            )}
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{ color: 'inherit' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      >
        <Box>
          {/* Primary Alert */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {primaryAlert.title}
              </Typography>
              <Typography variant="body2">
                {primaryAlert.message}
              </Typography>
            </Box>
            <Chip
              label={primaryAlert.metadata?.threshold || 'Alert'}
              size="small"
              color={primaryAlert.severity}
              sx={{ ml: 1, fontSize: '0.7rem' }}
            />
          </Box>

          {/* Primary Alert Actions */}
          {primaryAlert.actions && primaryAlert.actions.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
              {primaryAlert.actions.map((action, idx) => (
                <Button
                  key={idx}
                  size="small"
                  variant="outlined"
                  onClick={() => handleAlertAction(action)}
                  sx={{
                    color: 'inherit',
                    borderColor: 'currentColor',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  {action.label}
                </Button>
              ))}
              {primaryAlert.persistent && (
                <Button
                  size="small"
                  onClick={() => handleDismissAlert(primaryAlert.id, primaryAlert.type)}
                  sx={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  Hide for Now
                </Button>
              )}
            </Box>
          )}

          {/* Additional Alerts (Collapsible) */}
          <Collapse in={expanded}>
            {additionalAlerts.length > 0 && (
              <>
                <Divider sx={{ my: 2, backgroundColor: 'rgba(255,255,255,0.3)' }} />
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Additional Alerts:
                </Typography>
                <List dense sx={{ pt: 0 }}>
                  {additionalAlerts.map((alert, idx) => (
                    <ListItem
                      key={alert.id}
                      sx={{
                        px: 0,
                        py: 0.5,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start'
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <ListItemText
                          primary={alert.title}
                          secondary={alert.message}
                          primaryTypographyProps={{
                            variant: 'body2',
                            sx: { fontWeight: 'medium', color: 'inherit' }
                          }}
                          secondaryTypographyProps={{
                            variant: 'caption',
                            sx: { color: 'rgba(255,255,255,0.8)' }
                          }}
                        />
                        {alert.actions && alert.actions.length > 0 && (
                          <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5 }}>
                            {alert.actions.map((action, actionIdx) => (
                              <Button
                                key={actionIdx}
                                size="small"
                                variant="text"
                                onClick={() => handleAlertAction(action)}
                                sx={{
                                  color: 'inherit',
                                  fontSize: '0.7rem',
                                  minWidth: 'auto',
                                  p: 0.5
                                }}
                              >
                                {action.label}
                              </Button>
                            ))}
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ ml: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Chip
                          label={alert.metadata?.threshold || 'Alert'}
                          size="small"
                          color={alert.severity}
                          sx={{ fontSize: '0.6rem', height: '20px' }}
                        />
                        {alert.persistent && (
                          <Button
                            size="small"
                            onClick={() => handleDismissAlert(alert.id, alert.type)}
                            sx={{
                              color: 'inherit',
                              fontSize: '0.6rem',
                              minWidth: 'auto',
                              p: 0.25,
                              textDecoration: 'underline'
                            }}
                          >
                            Hide
                          </Button>
                        )}
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Collapse>
        </Box>
      </Alert>
    </Snackbar>
  );
}